import chroma from "chroma-js";
import { massageImageUrl } from "common/components/portable-renderers";
import { Appearance } from "common/generator/components/button-appearance";
import { isEmptyOrUndefined } from "common/Support";
import { CardStyle } from "common/wire";
import { useRuntimeTheme } from "hooks/use-runtime-theme";
import { GlideFC } from "lib/glide-fc";
import * as React from "react";
import { getScrollBarWidth } from "sharedUI/lib/scrollbar-width";
import { GlideIcon } from "sharedUI/ui/views/glide-icon/glide-icon";
import tw from "twin.macro";
import { WireButton } from "webapp/ui/views/wire-button/wire-button";
import { useResponsiveSizeClass } from "webapp/wire-renderers/desktop-wire-app";
import Img from "../img/img";

export interface ItemDescription {
    readonly title?: string;
    readonly subtitle?: string;
    readonly emphasis?: string;
    readonly body?: string;
    readonly image?: string;
    readonly secondaryAction?: string;
    readonly tertiaryAction?: string;
    readonly heroImage?: string;
    readonly onPrimary?: () => void;
    readonly onSecondary?: () => void;
    readonly onTertiary?: () => void;
}

function rangeForPage(page: number, numPages: number, size: number): (number | null)[] {
    let start = page - size;
    let end = page + size;

    const upper = size * 2;

    if (start < 0) {
        end += -start;
        start = 0;
    }
    if (end >= numPages) {
        const d = end - (numPages - 1);
        start = Math.max(0, start - d);
        end = numPages - 1;
    }

    const result: (number | null)[] = [];
    for (let x = start; x <= end; x++) {
        result.push(x);
    }

    if (result[0] !== 0) {
        result[0] = 0;
        result[1] = null;
    }

    if (numPages > upper && result[upper] !== numPages - 1) {
        result[upper] = numPages - 1;
        result[upper - 1] = null;
    }

    return result;
}

interface Props<T> {
    readonly cardStyle: CardStyle;
    readonly pageItems: readonly T[];
    readonly page: number;
    readonly numPages: number;
    readonly describeItem: (item: T) => ItemDescription;
    readonly onPageChange?: (page: number) => void;
}

interface CardProps {
    desc: ItemDescription;
}

const Card: React.VFC<CardProps> = p => {
    const { desc } = p;
    return (
        <div
            onClick={e => {
                desc.onPrimary?.();
                e.stopPropagation();
            }}
            css={[desc.onPrimary && tw`hover:shadow-lg-dark`]}
            tw="relative bg-n0 text-text-dark flex flex-col shadow-md-dark w-full rounded-lg overflow-hidden
                transition-shadow">
            <Img src={desc.image} tw="w-full filter brightness-75 h-40 object-cover bg-accent" />
            {desc.heroImage && (
                <Img
                    src={massageImageUrl(desc.heroImage, { thumbnail: false, width: 96, height: 96 }, undefined)}
                    tw="w-24 h-24 self-center -mt-12 flex-shrink-0 rounded-full object-cover border-2 z-10 border-solid
                        border-n0"
                />
            )}
            <div tw="flex-grow flex flex-col p-4">
                {desc.emphasis && (
                    <div tw="font-size[12px] line-height[20px] uppercase text-accent font-bold">{desc.emphasis}</div>
                )}
                {desc.title && <div tw="text-xl font-extrabold whitespace-pre-wrap">{desc.title}</div>}
                {desc.subtitle && <div tw="text-sm text-text-pale whitespace-pre-wrap">{desc.subtitle}</div>}
                <div tw="flex-grow" />
                <div tw="flex mt-4 all-child:(flex-1)">
                    {!isEmptyOrUndefined(desc.secondaryAction) && desc.onSecondary !== undefined && (
                        <WireButton
                            appearance={Appearance.Filled}
                            onClick={e => {
                                e.stopPropagation();
                                desc.onSecondary?.();
                            }}>
                            {desc.secondaryAction}
                        </WireButton>
                    )}
                    {!isEmptyOrUndefined(desc.tertiaryAction) && desc.onTertiary !== undefined && (
                        <WireButton
                            tw="not-first:ml-2"
                            appearance={Appearance.Bordered}
                            onClick={e => {
                                e.stopPropagation();
                                desc.onTertiary?.();
                            }}>
                            {desc.tertiaryAction}
                        </WireButton>
                    )}
                </div>
            </div>
        </div>
    );
};

const List: React.VFC<CardProps> = p => {
    const { desc } = p;
    return (
        <div
            onClick={e => {
                desc.onPrimary?.();
                e.stopPropagation();
            }}
            css={[desc.onPrimary && tw`hover:shadow-lg-dark`]}
            tw="relative bg-n0 p-6 text-text-dark flex shadow-md-dark w-full rounded-lg overflow-hidden
                transition-shadow">
            <Img
                src={massageImageUrl(desc.image, { thumbnail: false, width: 72, height: 72 }, undefined)}
                tw="width[72px] height[72px] mr-6 flex-shrink-0 object-cover bg-accent rounded-lg"
            />
            <div tw="flex-grow flex flex-col">
                {desc.emphasis && (
                    <div tw="font-size[12px] line-height[20px] uppercase text-accent font-bold">{desc.emphasis}</div>
                )}
                {desc.title && <div tw="text-lg font-extrabold whitespace-pre-wrap">{desc.title}</div>}
                {desc.subtitle && <div tw="text-sm text-text-pale whitespace-pre-wrap">{desc.subtitle}</div>}
            </div>
            <div tw="flex ml-4 self-center">
                {!isEmptyOrUndefined(desc.secondaryAction) && desc.onSecondary !== undefined && (
                    <WireButton
                        appearance={Appearance.Filled}
                        onClick={e => {
                            e.stopPropagation();
                            desc.onSecondary?.();
                        }}>
                        {desc.secondaryAction}
                    </WireButton>
                )}
                {!isEmptyOrUndefined(desc.tertiaryAction) && desc.onTertiary !== undefined && (
                    <WireButton
                        tw="not-first:ml-2"
                        appearance={Appearance.Bordered}
                        onClick={e => {
                            e.stopPropagation();
                            desc.onTertiary?.();
                        }}>
                        {desc.tertiaryAction}
                    </WireButton>
                )}
            </div>
        </div>
    );
};

const Table: React.VFC<CardProps> = p => {
    const { desc } = p;
    return (
        <div
            className="group"
            onClick={e => {
                desc.onPrimary?.();
                e.stopPropagation();
            }}
            css={[desc.onPrimary && tw`cursor-pointer hover:bg-bg-middle`]}
            tw="relative border-solid border-border-base bg-n0 height[52px] px-6 text-text-dark items-center flex
                w-full overflow-hidden transition-colors --button-opacity[0] hover:(--button-opacity[1])
                focus-within:(--button-opacity[1]) not-last:(border-b)">
            <Img
                src={massageImageUrl(desc.image, { thumbnail: true, width: 32, height: 32 }, undefined)}
                tw="w-8 h-8 mr-4 flex-shrink-0 object-cover bg-accent rounded-lg"
            />
            <div
                tw="flex-1 grid grid-cols-3 gap-2 text-sm text-text-pale all-child:(overflow-hidden whitespace-nowrap
                    overflow-ellipsis)">
                <div tw="text-text-dark font-semibold">{desc.title}</div>
                <div>{desc.subtitle}</div>
                <div>{desc.emphasis}</div>
            </div>
            <div tw="flex opacity[var(--button-opacity)] transition-opacity ml-4 items-center">
                {!isEmptyOrUndefined(desc.secondaryAction) && desc.onSecondary !== undefined && (
                    <WireButton
                        appearance={Appearance.Filled}
                        onClick={e => {
                            e.stopPropagation();
                            desc.onSecondary?.();
                        }}>
                        {desc.secondaryAction}
                    </WireButton>
                )}
                {!isEmptyOrUndefined(desc.tertiaryAction) && desc.onTertiary !== undefined && (
                    <WireButton
                        tw="not-first:ml-2"
                        appearance={Appearance.Bordered}
                        onClick={e => {
                            e.stopPropagation();
                            desc.onTertiary?.();
                        }}>
                        {desc.tertiaryAction}
                    </WireButton>
                )}
            </div>
        </div>
    );
};

const Article: React.VFC<CardProps> = p => {
    const { desc } = p;
    return (
        <div
            onClick={e => {
                desc.onPrimary?.();
                e.stopPropagation();
            }}
            css={[desc.onPrimary && tw`hover:shadow-lg-dark`]}
            tw="relative bg-n0 p-6 text-text-dark flex-col flex shadow-md-dark w-full rounded-lg overflow-hidden
                transition-shadow
                gp-md:flex-row">
            {desc.image && (
                <div
                    tw="hidden flex-shrink-0 mr-6
                        gp-md:block">
                    <Img
                        src={massageImageUrl(desc.image, { thumbnail: false, width: 208, height: 208 }, undefined)}
                        tw="object-cover bg-accent rounded-lg w-20 h-20
                            gp-lg:(w-32 h-32)
                            gp-xl:(w-44 h-44)
                            gp-2xl:(w-52 h-52)"
                    />
                </div>
            )}
            <div tw="flex-grow flex flex-col">
                <div tw="flex">
                    {desc.image && (
                        <div
                            tw="flex-shrink-0 mr-6
                                gp-md:hidden">
                            <Img
                                src={massageImageUrl(
                                    desc.image,
                                    { thumbnail: false, width: 208, height: 208 },
                                    undefined
                                )}
                                tw="w-16 h-16 object-cover bg-accent rounded-lg"
                            />
                        </div>
                    )}
                    <div tw="flex flex-col flex-1">
                        {desc.emphasis && (
                            <div
                                tw="font-size[11px] line-height[20px] uppercase text-accent font-bold
                                    gp-md:font-size[12px]">
                                {desc.emphasis}
                            </div>
                        )}
                        {desc.title && (
                            <h3
                                tw="text-base font-extrabold whitespace-pre-wrap
                                    gp-md:text-lg
                                    gp-lg:text-xl
                                    gp-xl:text-2xl">
                                {desc.title}
                            </h3>
                        )}
                        {desc.subtitle && <div tw="text-sm text-text-pale whitespace-pre-wrap">{desc.subtitle}</div>}
                    </div>
                </div>
                {desc.body && <p tw="text-sm mt-1.5 text-text-xpale whitespace-pre-wrap">{desc.body}</p>}
                <div tw="flex mt-3">
                    {!isEmptyOrUndefined(desc.secondaryAction) && desc.onSecondary !== undefined && (
                        <WireButton
                            appearance={Appearance.Filled}
                            onClick={e => {
                                e.stopPropagation();
                                desc.onSecondary?.();
                            }}>
                            {desc.secondaryAction}
                        </WireButton>
                    )}
                    {!isEmptyOrUndefined(desc.tertiaryAction) && desc.onTertiary !== undefined && (
                        <WireButton
                            tw="not-first:ml-2"
                            appearance={Appearance.Bordered}
                            onClick={e => {
                                e.stopPropagation();
                                desc.onTertiary?.();
                            }}>
                            {desc.tertiaryAction}
                        </WireButton>
                    )}
                </div>
            </div>
        </div>
    );
};

export function CardCollection<T>(p: Props<T>): React.ReactElement | null {
    const { describeItem, numPages, page, pageItems, onPageChange, cardStyle } = p;

    let style: React.CSSProperties | undefined;

    if (cardStyle !== CardStyle.Card) {
        style = {
            gridTemplateColumns: "repeat(1, minmax(0, 1fr))",
            width: "100%",
        };
    }

    const sizeClass = useResponsiveSizeClass();
    const scrollbarSize = React.useMemo(() => getScrollBarWidth(undefined), []);

    let extra = 0;
    switch (sizeClass) {
        case undefined:
        case "sm":
        case "md":
        case "lg":
            extra = scrollbarSize;
    }

    let content: React.ReactNode;
    if (cardStyle === CardStyle.Table) {
        content = (
            <div
                tw="relative shadow-md-dark rounded-lg overflow-hidden self-stretch"
                style={{ height: pageItems.length * 52 + extra }}>
                <div tw="absolute inset-0 overflow-x-auto overflow-y-hidden">
                    <div tw="flex flex-col min-width[800px]">
                        {pageItems.map((item, index) => {
                            const desc = describeItem(item);
                            const key = `${index}-${page}`;
                            return <Table key={key} desc={desc} />;
                        })}
                    </div>
                </div>
            </div>
        );
    } else {
        content = (
            <div
                style={style}
                css={[
                    tw`grid w-full grid-cols-1 grid-gap[12px]
                    gp-md:grid-cols-2
                    gp-xl:grid-cols-3
                    gp-2xl:grid-cols-4`,
                ]}>
                {pageItems.map((item, index) => {
                    const desc = describeItem(item);
                    const key = `${index}-${page}`;

                    let Component = Card;
                    if (cardStyle === CardStyle.Article) {
                        Component = Article;
                    } else if (cardStyle === CardStyle.List) {
                        Component = List;
                    }
                    return <Component key={key} desc={desc} />;
                })}
            </div>
        );
    }

    return (
        <div tw="flex flex-col items-center">
            {content}
            {numPages > 1 && <Pager numPages={numPages} page={page} onPageChange={onPageChange} />}
        </div>
    );
}

interface PagerProps {
    readonly page: number;
    readonly numPages: number;
    readonly onPageChange?: (page: number) => void;
    readonly className?: string;
}

export const Pager: GlideFC<PagerProps> = p => {
    const { numPages, page, onPageChange } = p;

    const theme = useRuntimeTheme();
    const sizeClass = useResponsiveSizeClass();

    let numItems = 2;
    if (sizeClass === "md") {
        numItems = 3;
    } else if (sizeClass === "lg") {
        numItems = 4;
    } else if (sizeClass === "xl" || sizeClass === "2xl") {
        numItems = 5;
    }

    return (
        <div className={p.className} tw="flex mt-4 self-stretch justify-center">
            <WireButton
                appearance={Appearance.Bordered}
                disabled={page === 0}
                tw="max-width[320px] rounded-lg px-2! mr-2 h-9
                    gp-sm:px-3
                    gp-lg:(mr-6 flex-grow)"
                onClick={() => onPageChange?.(Math.max(0, page - 1))}>
                <div
                    tw="hidden
                        gp-lg:block">
                    Previous
                </div>
                <div tw="gp-lg:hidden">
                    <GlideIcon iconSize={16} icon="st-caret" rotateDeg={90} kind="stroke" />
                </div>
            </WireButton>
            <div tw="flex shadow-md-dark rounded-lg overflow-hidden all-child:(px-1 py-2 bg-n0 h-9)">
                {rangeForPage(page, numPages, numItems).map(x => (
                    <button
                        onClick={() => x !== null && onPageChange?.(x)}
                        style={{
                            backgroundColor: x === page ? chroma(theme.primaryAccentColor).alpha(0.1).css() : undefined,
                        }}
                        tw="text-text-base last:(border-r-0) hover:underline focus-visible:underline border-0 border-r
                            border-solid border-border-base"
                        css={[x === page && tw`font-bold text-accent`]}>
                        <div
                            tw="w-5 text-center
                                gp-sm:w-7">
                            {x === null ? "…" : x + 1}
                        </div>
                    </button>
                ))}
            </div>
            <WireButton
                appearance={Appearance.Bordered}
                disabled={page === numPages - 1}
                onClick={() => onPageChange?.(Math.min(numPages - 1, page + 1))}
                tw="max-width[320px] rounded-lg px-2! ml-2 h-9
                    gp-sm:px-3
                    gp-lg:(ml-6 flex-grow)">
                <div
                    tw="hidden
                        gp-lg:block">
                    Next
                </div>
                <div tw="gp-lg:hidden">
                    <GlideIcon iconSize={16} icon="st-caret" rotateDeg={-90} kind="stroke" />
                </div>
            </WireButton>
        </div>
    );
};
